/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnChanges, SimpleChanges, input, model } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Logger } from '../../../logger/logger';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TooltipDirective } from '../../../components/common-tooltip/tooltip.directive';
import { PluralPipe } from '../../../pipes/plural.pipe';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'vitamui-common-archive-unit-count',
  templateUrl: './archive-unit-count.component.html',
  styleUrls: ['./archive-unit-count.component.scss'],
  imports: [MatProgressSpinner, TooltipDirective, PluralPipe, TranslatePipe],
})
export class ArchiveUnitCountComponent implements OnChanges {
  private readonly logger = inject(Logger);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly search = input<Observable<number>>(undefined);
  readonly archiveUnitCount = model(0);
  readonly selectedArchiveUnitCount = model(0);
  readonly pending = model<boolean>(undefined);
  readonly threshold = input<number>(undefined);
  readonly allChecked = input(false);

  canLoadExactCount = false;
  exactCountLoaded = false;
  displaySelectedArchiveUnitCount = true;

  private readonly subscriptions = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    const { selectedArchiveUnitCount, archiveUnitCount, threshold, search } = changes;

    if (search) {
      this.exactCountLoaded = false;
    }
    if (archiveUnitCount || selectedArchiveUnitCount || threshold || search) {
      this.update();
    }
  }

  private isValidExactCount(exactCount: number): boolean {
    return exactCount >= 0;
  }

  private shouldChangeArchiveUnitCount(exactCount: number): boolean {
    return this.archiveUnitCount() !== exactCount && this.isValidExactCount(exactCount);
  }

  private handleExactCount(exactCount: number) {
    this.exactCountLoaded = this.isValidExactCount(exactCount);
    this.update();
    if (this.shouldChangeArchiveUnitCount(exactCount)) {
      this.archiveUnitCount.set(exactCount);

      if (this.allChecked()) {
        this.selectedArchiveUnitCount.set(exactCount);
      }
    }
    // Called from the search subscription: refresh the view (zoneless)
    this.cdr.markForCheck();
  }

  private update(): void {
    this.canLoadExactCount = !this.exactCountLoaded && this.archiveUnitCount() >= this.threshold();
    this.displaySelectedArchiveUnitCount = this.shouldDisplaySelectedArchiveUnitCount();
  }

  loadExactCount() {
    const updatePending = (value: boolean) => {
      this.pending.set(value);
      this.cdr.markForCheck();
    };
    const notPending = () => {
      updatePending(false);
    };

    const search = this.search();
    if (!search) {
      this.logger.error(this, 'Cannot load exact count. No search observable provided');
      return;
    }

    updatePending(true);
    this.subscriptions.add(
      search.pipe(tap(notPending, notPending)).subscribe(
        (exactCount: number) => {
          this.handleExactCount(exactCount);
        },
        (error: HttpErrorResponse) => {
          this.logger.error(this, error.message);
          this.handleExactCount(-1);
        },
      ),
    );
  }

  private shouldDisplaySelectedArchiveUnitCount(): boolean {
    const isInclusiveMode = !this.allChecked();
    const extactCountIsKnown = this.exactCountLoaded;
    const knownCountUnderThreshold = this.archiveUnitCount() < this.threshold();
    return isInclusiveMode || extactCountIsKnown || knownCountUnderThreshold;
  }
}
